import React, { useState } from 'react';
import { Swords, Shield, Trophy, Coins, ArrowLeft } from 'lucide-react';
import { PetSelection } from './PetSelection';
import { ItemSelection } from './ItemSelection';
import { BattleArena } from './BattleArena';
import { usePets } from '../../hooks/usePets';
import { useItems } from '../../hooks/useItems';
import type { User } from '../../types/user';
import type { UserPet } from '../../types/pets';
import type { UserItem } from '../../types/items';

interface PvPCombatProps {
  user: User | null;
  onBack: () => void;
}

export function PvPCombat({ user, onBack }: PvPCombatProps) {
  const [selectedPets, setSelectedPets] = useState<UserPet[]>([]);
  const [selectedItems, setSelectedItems] = useState<UserItem[]>([]);
  const [wagerAmount, setWagerAmount] = useState(1000);
  const { userPets } = usePets(user?.walletAddress);
  const { userItems } = useItems(user?.walletAddress);
  const [step, setStep] = useState<'select' | 'battle'>('select');

  const handleStartBattle = () => {
    if (!selectedItems.length && !selectedPets.length) {
      return; // Require at least one item or pet
    }
    setStep('battle');
  };

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Kingdom
      </button>

      {step === 'select' ? (
        <>
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold font-serif mb-4 bg-gradient-to-r from-red-500 to-orange-500 text-transparent bg-clip-text">
              Battle Arena
            </h1>
            <p className="text-xl text-amber-200 font-serif">
              Choose your champions and equipment for battle!
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Select at least one pet or item to begin
            </p>
          </div>

          {/* Selection Phase */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pet Selection */}
            <div className="bg-gray-900 rounded-xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-6">
                <Swords className="w-6 h-6 text-amber-500" />
                <h2 className="text-xl font-bold text-white">Select Your Champions (Optional)</h2>
              </div>
              <PetSelection
                pets={userPets}
                selectedPets={selectedPets}
                onSelect={setSelectedPets}
                maxSelections={3}
              />
            </div>

            {/* Item Selection */}
            <div className="bg-gray-900 rounded-xl p-6 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-6 h-6 text-amber-500" />
                <h2 className="text-xl font-bold text-white">Choose Your Equipment</h2>
              </div>
              <ItemSelection
                items={userItems}
                selectedItems={selectedItems}
                onSelect={setSelectedItems}
                maxSelections={3}
              />
            </div>
          </div>

          {/* Wager Selection */}
          <div className="bg-gray-900 rounded-xl p-6 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-6">
              <Coins className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl font-bold text-white">Set Your Wager</h2>
            </div>
            <div className="space-y-4">
              <input
                type="range"
                min="1000"
                max={user?.points || 1000}
                value={wagerAmount}
                onChange={(e) => setWagerAmount(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400">
                <span>1,000 coins</span>
                <span>{wagerAmount.toLocaleString()} coins</span>
                <span>{user?.points.toLocaleString()} coins</span>
              </div>
            </div>
          </div>

          {/* Start Battle Button */}
          <div className="flex justify-center">
            <button
              onClick={handleStartBattle}
              disabled={!selectedItems.length && !selectedPets.length}
              className="px-8 py-4 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-lg
                hover:from-yellow-500 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed
                font-bold text-lg"
            >
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Start Battle
              </div>
            </button>
          </div>
        </>
      ) : (
        <BattleArena
          user={user}
          pets={selectedPets}
          items={selectedItems}
          wagerAmount={wagerAmount}
          onBack={() => setStep('select')}
        />
      )}
    </div>
  );
}